import {
  Box,
  Button,
  FormHelperText,
  Paper,
  TextField,
  Typography
} from '@material-ui/core'

import * as R from 'ramda'
import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import FormHtmlEditor from '../../components/form/HtmlEditor'
import FormSingleFileDropZone from '../../components/form/SingleFileDropZone'
import { BlogDetailInterface } from '../../interfaces/blog'
import { slugify } from '../../utils/common'

const StyledPaperContainer = styled(Paper)`
  padding: 10px;
  margin: 20px 10px;
`

const StyledLabel = styled.label`
  font-weight: 600;
  font-size: 16px;
`

const StyledLabelContainer = styled.div`
  width: 100%;
  margin-bottom: 15px;
`

const StyledFormGroupContainer = styled.div`
  margin: 20px 20% 20px 20%;
`

const StyledFormFieldContainer = styled.div`
  margin: 15px 0px 15px 0px;
  padding: 0px;
`

const StyledErrorMessageContainer = styled.div`
  height: 20px;
  width: 100%;
  display: flex;
  align-items: center;
`

const StyledFormSubTitle = styled.div`
  margin-left: 15px;
`

// TODO: refactor to perform scroll inside the form
const StyledFormContainer = styled.div`
  padding: 0px 10px;
  overflow-y: auto;
  max-height: 70vh;
`

const StyledFormHelperTextContainer = styled.div`
  margin: 0px 0px 15px 5px;
`

const StyledSlugNameFieldContainer = styled.div`
  display: flex;
`

const FormLabel = ({ name }: { name: string }) => (
  <StyledLabelContainer>
    <StyledLabel>{name}</StyledLabel>
  </StyledLabelContainer>
)

const FormErrorMessage = ({ message }: { message: string }) => (
  <Box component="div" display="line" fontSize={10} color="red">
    {message}
  </Box>
)

const BlogFormMainBody = ({
  blogDetail
}: {
  blogDetail: BlogDetailInterface
}) => {
  const { errors, control, setValue, getValues } = useFormContext()
  const textFieldInputProps = { style: { fontSize: 13 } }

  const handleGenerateSlugName = () => {
    setValue('slugName', R.slice(0, 50, slugify(getValues('title'))))
  }

  return (
    <StyledFormContainer>
      <StyledPaperContainer elevation={3}>
        <StyledFormSubTitle>
          <Typography variant="subtitle1">Basic Information</Typography>
        </StyledFormSubTitle>
        <StyledFormGroupContainer>
          <StyledFormFieldContainer>
            <FormLabel name="Slug Name" />
            <StyledFormHelperTextContainer>
              <FormHelperText>Note:</FormHelperText>
              <FormHelperText>
                1. slug name must be composed of character and digit connected
                together with a dash
              </FormHelperText>
              <FormHelperText>2. slug name max length is 50</FormHelperText>
              <FormHelperText>Example Input: "this-is-a-test"</FormHelperText>
            </StyledFormHelperTextContainer>
            <StyledSlugNameFieldContainer>
              <Controller
                name="slugName"
                control={control}
                defaultValue={R.propOr('', 'slugName', blogDetail)}
                rules={{
                  required: {
                    value: true,
                    message: '* Your input is required'
                  },
                  maxLength: {
                    value: 50,
                    message: '* The slug name is over the limited length of 50'
                  }
                }}
                as={
                  <TextField
                    required
                    id="outlined-required"
                    label="Required"
                    variant="outlined"
                    defaultValue={R.propOr('', 'slugName', blogDetail)}
                    inputProps={textFieldInputProps}
                    fullWidth
                  />
                }
              />
              <Button
                variant="outlined"
                color="primary"
                size="small"
                style={{ marginLeft: '15px' }}
                onClick={handleGenerateSlugName}
              >
                Generate Slug Name
              </Button>
            </StyledSlugNameFieldContainer>
            <StyledErrorMessageContainer>
              {errors.slugName && (
                <FormErrorMessage
                  message={errors?.slugName?.message as string}
                />
              )}
            </StyledErrorMessageContainer>
          </StyledFormFieldContainer>
          <StyledFormFieldContainer>
            <FormLabel name="Title" />
            <Controller
              name="title"
              control={control}
              defaultValue={R.propOr('', 'title', blogDetail)}
              rules={{
                required: {
                  value: true,
                  message: '* Your input is required'
                }
              }}
              as={
                <TextField
                  required
                  id="outlined-required"
                  label="Required"
                  variant="outlined"
                  defaultValue={R.propOr('', 'title', blogDetail)}
                  inputProps={textFieldInputProps}
                  fullWidth
                />
              }
            />
            <StyledErrorMessageContainer>
              {errors.title && (
                <FormErrorMessage message={errors?.title?.message as string} />
              )}
            </StyledErrorMessageContainer>
          </StyledFormFieldContainer>
          <StyledFormFieldContainer>
            <FormLabel name="Subtitle" />
            <Controller
              name="subtitle"
              control={control}
              defaultValue={R.propOr('', 'subtitle', blogDetail)}
              render={({ onChange }) => (
                <TextField
                  onChange={onChange}
                  required
                  id="outlined"
                  variant="outlined"
                  defaultValue={R.propOr('', 'subtitle', blogDetail)}
                  inputProps={textFieldInputProps}
                  fullWidth
                />
              )}
            />
          </StyledFormFieldContainer>
        </StyledFormGroupContainer>
      </StyledPaperContainer>
      <StyledPaperContainer>
        <StyledFormSubTitle>
          <Typography variant="subtitle1">Contents</Typography>
        </StyledFormSubTitle>
        <StyledFormGroupContainer>
          <StyledFormFieldContainer>
            <FormHtmlEditor
              fieldName="content"
              fieldValue={R.prop('content', blogDetail)}
            />
          </StyledFormFieldContainer>
        </StyledFormGroupContainer>
      </StyledPaperContainer>
      <StyledPaperContainer>
        <StyledFormSubTitle>
          <Typography variant="subtitle1">Images</Typography>
        </StyledFormSubTitle>
        <StyledFormGroupContainer>
          <StyledFormFieldContainer>
            <FormLabel name="Mini Icon Image" />
            <FormSingleFileDropZone fieldName="miniIconImageFile" />
          </StyledFormFieldContainer>
        </StyledFormGroupContainer>
      </StyledPaperContainer>
    </StyledFormContainer>
  )
}

export default BlogFormMainBody
